import { useCallback, useEffect, useState } from "react";
import { AppLayout } from "@/components/layout/AppLayout";
import { PageHeader } from "@/components/layout/PageHeader";
import { PageContainer } from "@/components/layout/PageContainer";
import { DataTable, type DataTableColumn } from "@/components/ui/data-table";
import { EmptyState } from "@/components/ui/empty-state";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { useToast } from "@/contexts/ToastContext";
import { auditApi, type AuditLogEntry } from "@/lib/api/audit";
import { ShieldCheck } from "lucide-react";

const LOG_LIMIT = 100;

function formatActor(log: AuditLogEntry): string {
  const actor = log.staff ?? log.user;
  if (!actor) return "—";
  const name = [actor.firstName, actor.lastName].filter(Boolean).join(" ").trim();
  return name || actor.email || "—";
}

function formatWhen(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const columns: DataTableColumn<AuditLogEntry>[] = [
  {
    id: "createdAt",
    header: "When",
    cell: (row) => (
      <span className="whitespace-nowrap text-sm text-slate-700">{formatWhen(row.createdAt)}</span>
    ),
  },
  {
    id: "action",
    header: "Action",
    cell: (row) => (
      <span className="font-mono text-xs uppercase tracking-wide text-slate-800">{row.action}</span>
    ),
  },
  {
    id: "resourceType",
    header: "Resource",
    cell: (row) => (
      <div className="min-w-0">
        <p className="truncate text-sm text-slate-900">{row.resourceType}</p>
        {row.resourceId ? (
          <p className="truncate font-mono text-xs text-slate-500">{row.resourceId}</p>
        ) : null}
      </div>
    ),
  },
  {
    id: "actor",
    header: "Actor",
    cell: (row) => <span className="text-sm text-slate-700">{formatActor(row)}</span>,
  },
  {
    id: "description",
    header: "Description",
    cell: (row) => (
      <span className="line-clamp-2 text-sm text-slate-600">{row.description || "—"}</span>
    ),
  },
];

export default function SecurityAuditPage() {
  const { error } = useToast();
  const [logs, setLogs] = useState<AuditLogEntry[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await auditApi.getLogs({ limit: LOG_LIMIT });
      setLogs(data);
    } catch {
      error("Could not load activity logs.");
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [error]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <AppLayout>
      <PageContainer width="wide">
        <PageHeader
          title="Security & Audit"
          description="Recent system and user activity for this library."
          showSearch={false}
          titleSize="md"
          breadcrumbItems={[
            { label: "Dashboard", href: "/dashboard" },
            { label: "Security & Audit" },
          ]}
        />

        {loading ? (
          <div className="flex justify-center py-16">
            <LoadingSpinner />
          </div>
        ) : (
          <DataTable
            columns={columns}
            data={logs}
            rowKey={(row) => row.id}
            emptyState={
              <EmptyState
                icon={<ShieldCheck className="h-6 w-6 text-slate-400" />}
                title="No activity yet"
                description="Audit entries will appear here as staff and patrons use the system."
              />
            }
          />
        )}
      </PageContainer>
    </AppLayout>
  );
}
